import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useLocale, useLocalPathname } from '../i18n/Link'
import { LOCALES, localizePath } from '../i18n/locales'

const SITE_URL = 'https://cmdesk.help'
const SITE_NAME = 'CMDesk Remote Desktop'
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`
const X_DEFAULT_LOCALE = 'en'

interface SEOProps {
  title: string
  description: string
  keywords?: string
  canonical?: string
  isHome?: boolean
  ogType?: string
  ogImage?: string
  noindex?: boolean
  structuredData?: object | object[]
}

/** Rewrites site URLs inside a schema object so they point at the active language. */
function localizeSchema(value: unknown, code: string): unknown {
  if (Array.isArray(value)) return value.map((v) => localizeSchema(v, code))
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (typeof v === 'string' && v.startsWith(SITE_URL + '/') && !/\.(png|svg|jpg|webp|txt)$/i.test(v) && !v.includes('{')) {
        out[k] = SITE_URL + localizePath(code, v.slice(SITE_URL.length))
      } else if (typeof v === 'string' && v === SITE_URL && k !== 'url') {
        out[k] = v
      } else {
        out[k] = localizeSchema(v, code)
      }
    }
    return out
  }
  return value
}

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  isHome = false,
  ogType = 'website',
  ogImage,
  noindex = false,
  structuredData,
}: SEOProps) {
  const { t } = useTranslation('common')
  const locale = useLocale()
  const localPath = useLocalPathname()
  const path = canonical ?? localPath

  const fullTitle = isHome ? `${SITE_NAME} | ${t('seo.homeTitle')}` : `${title} | ${SITE_NAME}`
  const url = `${SITE_URL}${localizePath(locale.code, path)}`
  const image = ogImage ?? (locale.code === 'tr' ? DEFAULT_IMAGE : `${SITE_URL}/og/${locale.code}.png`)

  const schemaArray = (structuredData ? (Array.isArray(structuredData) ? structuredData : [structuredData]) : [])
    .map((s) => localizeSchema(s, locale.code) as Record<string, unknown>)
    .map((s) => {
      const type = s['@type']
      if (type === 'Organization' && 'description' in s) s.description = t('seo.orgDescription')
      if (type === 'SoftwareApplication') {
        s.description = t('seo.appDescription')
        s.inLanguage = locale.htmlLang
        s.featureList = t('seo.featureList', { returnObjects: true })
        if (s.offers && typeof s.offers === 'object') (s.offers as Record<string, unknown>).description = t('seo.offerFree')
      }
      if (type === 'WebSite' || type === 'FAQPage' || type === 'WebPage') s.inLanguage = locale.htmlLang
      return s
    })

  return (
    <Helmet>
      <html lang={locale.htmlLang} dir={locale.dir} />
      {locale.font && (
        <link rel="stylesheet" href={`https://fonts.googleapis.com/css2?family=${locale.font}&display=swap`} />
      )}

      {/* Temel Meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {!noindex && (
        <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />
      )}

      {/* Dil alternatifleri */}
      {!noindex &&
        LOCALES.map((l) => (
          <link key={l.code} rel="alternate" hrefLang={l.hreflang} href={`${SITE_URL}${localizePath(l.code, path)}`} />
        ))}
      {!noindex && (
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${localizePath(X_DEFAULT_LOCALE, path)}`} />
      )}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={locale.ogLocale} />
      {LOCALES.filter((l) => l.code !== locale.code).map((l) => (
        <meta key={l.code} property="og:locale:alternate" content={l.ogLocale} />
      ))}
      <meta property="og:url" content={url} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@cmdesk" />

      {/* Structured Data */}
      {schemaArray.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  )
}

// Reusable structured data generators. Text fields are localized inside <SEO>.
export const schemas = {
  organization: {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'CMDesk',
    url: SITE_URL,
    logo: `${SITE_URL}/brand/icon-512.png`,
    description: 'AnyDesk ve TeamViewer alternatifi, hızlı ve güvenli uzak masaüstü çözümü.',
    foundingDate: '2020',
    founder: {
      '@type': 'Person',
      name: 'Doğan Cihat Kaya',
    },
    sameAs: ['https://github.com/cmappseu', 'https://cmapps.eu'],
    parentOrganization: {
      '@type': 'Organization',
      name: 'CMApps',
      url: 'https://cmapps.eu',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'destek@cmdesk.help',
      contactType: 'customer support',
      availableLanguage: ['Turkish', 'English', 'German', 'French', 'Spanish', 'Portuguese', 'Russian', 'Arabic', 'Chinese', 'Japanese', 'Hindi', 'Indonesian'],
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Ankara',
      addressCountry: 'TR',
    },
  },

  website: {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/help?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  },

  softwareApp: {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: SITE_NAME,
    operatingSystem: 'Windows, macOS, Linux, Android',
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Remote Desktop',
    softwareVersion: '1.5.5',
    downloadUrl: `${SITE_URL}/download`,
    installUrl: `${SITE_URL}/download`,
    screenshot: `${SITE_URL}/product/02-masaustu.png`,
    image: `${SITE_URL}/og-image.png`,
    featureList: [] as string[],
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: 'Ücretsiz plan mevcut',
      availability: 'https://schema.org/InStock',
    },
    publisher: { '@type': 'Organization', name: 'CMDesk', url: SITE_URL },
    description: '',
    url: SITE_URL,
  },

  breadcrumb: (items: { name: string; url: string }[]) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.url}`,
    })),
  }),

  faq: (questions: { q: string; a: string }[]) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  }),
}
